/**
 * Enhanced Toast Messages for Contract Draft Module
 * 
 * Centralized toast messages with professional language,
 * contextual feedback, and optional action suggestions.
 */

export const CONTRACT_DRAFT_TOAST_MESSAGES = {
  // ===== DASHBOARD TOASTS =====
  DASHBOARD: {
    // Data Loading
    DATA_LOADING: "Loading contract dashboard...",
    DATA_LOADED: "Dashboard data refreshed successfully",
    DATA_LOAD_ERROR: "Unable to load dashboard. Please refresh and try again.",
    
    // Assignment Operations
    ASSIGNED_SUCCESS: (assigneeName: string) => 
      `Contract assigned to ${assigneeName}`,
    UNASSIGNED_SUCCESS: "Contract assignment removed",
    ASSIGNMENT_ERROR: "Unable to update assignment. Please try again.",
    
    // Status Updates
    STATUS_UPDATED: (newStatus: string) => 
      `Status updated to ${newStatus}`,
    STATUS_UPDATE_ERROR: "Failed to update status. Please try again.",
    
    // Stats Refresh
    STATS_REFRESHED: "Statistics updated",
    STATS_REFRESH_ERROR: "Unable to refresh statistics",
    
    // Polling
    NEW_REQUESTS_FOUND: "New contract requests available",
  },

  // ===== CONTRACT CREATION (INITIATION PAGE) =====
  CREATION: {
    LOADING_TYPES: "Loading contract types...",
    TYPES_LOAD_ERROR: "Unable to load contract types. Using default.",
    CREATE_START: "Creating contract request...",
    CREATE_SUCCESS: "Contract request created successfully. AI is now extracting data from your documents. Redirecting...",
    CREATE_ERROR: "Failed to create contract request. Please review your inputs and try again.",
    
    // Validation
    PROJECT_REQUIRED: "Project name is required",
    REQUESTOR_REQUIRED: "Requestor name is required",
    DRIVE_LINK_REQUIRED: "Google Drive folder link is required",
    DRIVE_LINK_INVALID: "Drive link is not a valid folder. Fix the field before submitting.",
    DRIVE_LINK_VALIDATING: "Still verifying the Drive folder — please wait a moment.",
  },

  // ===== DRAFTING DETAIL PAGE =====
  DRAFTING: {
    // Loading
    LOADING_DETAILS: "Loading contract details...",
    DETAILS_LOADED: "Contract details loaded",
    DETAILS_LOAD_ERROR: "Unable to load contract details. Please refresh.",
    
    // Saving Changes
    SAVING_CHANGES: "Saving your changes...",
    CHANGES_SAVED: "Changes saved successfully",
    SAVE_ERROR: "Failed to save changes. Please try again.",
    
    // Document Management
    UPLOADING_DOCS: "Uploading documents...",
    UPLOAD_SUCCESS: "Documents uploaded successfully",
    UPLOAD_ERROR: "Failed to upload documents",
    UPLOAD_DUPLICATE_ERROR: (fileName: string) => 
      `File already exists: ${fileName}. Please delete the existing file first or upload a file with a different name.`,
    SUBMIT_DOCS_START: "Submitting documents for AI processing...",
    SUBMIT_DOCS_SUCCESS: "Documents submitted successfully. AI extraction will process them shortly.",
    SUBMIT_DOCS_ERROR: "Failed to submit documents for processing",
    DELETE_SUCCESS: "Document removed successfully",
    DELETE_ERROR: "Failed to delete document",
    DOWNLOAD_SUCCESS: "Download started",
    DOWNLOAD_ERROR: "Failed to download document",
    
    // File Validation
    FILE_TYPE_ERROR: (fileName: string) => 
      `File type not supported: ${fileName}. Please use PDF, Word, Excel, or text files.`,
    FILE_SIZE_ERROR: (fileName: string) => 
      `File too large: ${fileName} (max 25 MB)`,
    
    // Template Preview
    LOADING_TEMPLATE: "Loading template preview...",
    TEMPLATE_LOAD_ERROR: "Unable to load template preview",
    
    // Draft Generation
    GENERATION_STARTING: "Draft generation has been initiated. Please come back after a few minutes to see the results.",
    GENERATION_IN_PROGRESS: "A draft is already being generated. Please wait for it to complete.",
    GENERATION_SUCCESS: (projectName?: string) =>
      projectName
        ? `Draft for "${projectName}" generated successfully! Please resolve all comments in the draft before requesting a review.`
        : "Draft generated successfully! Please resolve all comments in the draft before requesting a review.",
    GENERATION_ERROR: "Draft generation failed. Please try again or contact support.",
    VALIDATION_ERROR: "Please fill all required fields before generating a draft",
    DRAFT_READY_NOTIFICATION: (projectName?: string) =>
      projectName
        ? `Draft for "${projectName}" is ready! Click to view.`
        : "Your contract draft is ready! Click to view.",
    DRAFT_FAILED_NOTIFICATION: (projectName?: string) =>
      projectName
        ? `Draft generation failed for "${projectName}". Click to view details.`
        : "Draft generation failed. Click to view details.",
    
    // Review Requests
    REVIEW_SENDING: "Sending review request...",
    REVIEW_SUCCESS: "Review request sent successfully",
    REVIEW_ERROR: "Failed to send review request",
    
    // Completion
    MARKING_COMPLETE: "Marking contract as complete...",
    COMPLETE_SUCCESS: "Contract marked as complete",
    COMPLETE_ERROR: "Failed to mark contract as complete",
  },

  // ===== SUPPORTING DOCUMENTS SECTION =====
  DOCUMENTS: {
    DROP_ZONE_TEXT: "Drag documents here or click to upload",
    FILE_SELECTED: (count: number) => `${count} file(s) selected`,
    FILE_TYPE_INVALID: (types: string) => 
      `File type not allowed. Supported: ${types}`,
    FILE_SIZE_INVALID: (fileName: string) => 
      `File exceeds 25 MB limit: ${fileName}`,
    UPLOAD_STARTING: "Starting upload...",
  },

  // ===== GENERIC ERRORS =====
  ERRORS: {
    NETWORK_ERROR: "Network error. Please check your connection.",
    SERVER_ERROR: "Server error. Please try again later.",
    UNAUTHORIZED: "You don't have permission to perform this action.",
    NOT_FOUND: "The requested resource was not found.",
    UNKNOWN_ERROR: "An unexpected error occurred. Please try again.",
  },
};

// ===== TOAST CONFIG OPTIONS =====
export const TOAST_CONFIG = {
  SUCCESS: {
    autoClose: 4000,
    closeButton: true,
  },
  ERROR: {
    autoClose: 5000,
    closeButton: true,
  },
  WARNING: {
    autoClose: 4500,
    closeButton: true,
  },
  INFO: {
    autoClose: 3000,
    closeButton: true,
  },
  LOADING: {
    autoClose: false as false,
    closeButton: false,
  },
};
